import React from 'react';
import { Card, CardHeader, EmptyState } from '@opendate-app/setlist';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { getActivePreset } from './DateRangePicker';
import { compactCurrencyAxis } from '../utils/reportingUtils';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const SERIES_COLORS = [
  'var(--sl-fg-brand, #1982C4)', '#FAB714', '#8AC926', '#6A4C93', '#2CA8A0',
  '#F47C2C', 'var(--sl-text-quaternary, #6B7280)', '#E86A92',
];

const CURRENT_PERIOD_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This Week',
  last_week: 'Last Week',
  this_month: 'This Month',
  last_month: 'Last Month',
  month_to_date: 'Month to Date',
  this_year: 'This Year',
  last_year: 'Last Year',
  year_to_date: 'Year to Date',
  all_time: 'All Time',
};

const PRIOR_PERIOD_LABELS = {
  today: 'Yesterday',
  yesterday: 'Day Before',
  this_week: 'Last Week',
  last_week: 'Prior Week',
  this_month: 'Last Month',
  last_month: 'Prior Month',
  month_to_date: 'Last Month to Date',
  this_year: 'Last Year',
  last_year: 'Prior Year',
  year_to_date: 'Last Year to Date',
  all_time: 'Prior Period',
};

const ChartTooltip = ({ active, payload, label, isCurrency, countLabel }) => {
  if (!active || !payload || !payload.length) return null;

  const isMultiSeries = payload.length > 1;
  const visibleEntries = isMultiSeries
    ? payload.filter((entry) => entry.value > 0)
    : payload;
  const hiddenCount = payload.length - visibleEntries.length;

  return (
    <div style={{ background: '#000', borderRadius: 8, padding: 12, minWidth: 140, maxHeight: 280, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <p style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, margin: '0 0 8px 0', flexShrink: 0 }}>{label}</p>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {visibleEntries.map((entry, idx) => {
          const dataPoint = entry.payload || {};
          const isPrior = entry.dataKey === 'prior_value';
          const countKey = `${entry.dataKey}_count`;
          const count = dataPoint[countKey] != null
            ? dataPoint[countKey]
            : (isPrior ? dataPoint.prior_count : dataPoint.count);
          const revenue = isCurrency
            ? entry.value
            : (isPrior ? (dataPoint.prior_revenue != null ? dataPoint.prior_revenue : null) : (dataPoint.revenue != null ? dataPoint.revenue : null));

          return (
            <div key={idx} style={{ marginBottom: idx < visibleEntries.length - 1 ? 4 : 0 }}>
              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
                {isMultiSeries && (
                  <span style={{ color: '#a1a1aa', fontSize: 12 }}>{entry.name}:</span>
                )}
                {isCurrency && (
                  <span style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, fontWeight: 600 }}>
                    {currencyFormatter.format(entry.value)}
                  </span>
                )}
                {!isCurrency && count != null && (
                  <span style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, fontWeight: 600 }}>
                    {count.toLocaleString()} {countLabel || ''}
                  </span>
                )}
              </div>
              {isCurrency && count != null && (
                <div style={{ paddingLeft: 14, marginTop: 2 }}>
                  <span style={{ color: '#a1a1aa', fontSize: 12 }}>
                    {count.toLocaleString()} {countLabel || ''}
                  </span>
                </div>
              )}
              {!isCurrency && revenue != null && (
                <div style={{ paddingLeft: 14, marginTop: 2 }}>
                  <span style={{ color: '#a1a1aa', fontSize: 12 }}>
                    {currencyFormatter.format(revenue)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {hiddenCount > 0 && (
        <p style={{ color: 'var(--sl-text-quaternary, #71717a)', fontSize: 11, margin: '6px 0 0 0', flexShrink: 0 }}>
          +{hiddenCount} more with $0
        </p>
      )}
    </div>
  );
};

const SalesCard = ({
  title,
  titleLink,
  metricValue,
  metricLabel,
  description,
  chartData,
  chartColor,
  chartType,
  isCurrency,
  countLabel,
  seriesData,
  seriesNames,
  priorChartData,
  dateRange,
}) => {
  const hasMultiSeries = seriesData && seriesData.length > 0 && seriesNames && seriesNames.length > 1;
  const activeSeriesNames = hasMultiSeries
    ? seriesNames.filter((name) => seriesData.some((row) => row[name] > 0))
    : [];
  const hasPrior = !hasMultiSeries && priorChartData && priorChartData.length > 0;
  const displayData = hasMultiSeries ? seriesData : (hasPrior
    ? chartData.map((row, idx) => {
        const prior = priorChartData[idx] || {};
        return {
          ...row,
          prior_value: prior.value || 0,
          prior_count: prior.count || 0,
          prior_revenue: prior.revenue != null ? prior.revenue : null,
        };
      })
    : chartData);
  const resolvedChartType = chartType || (displayData.length <= 2 ? 'bar' : 'line');

  const activePreset = dateRange ? getActivePreset(dateRange) : null;
  const currentLabel = (activePreset && CURRENT_PERIOD_LABELS[activePreset]) || 'Current Period';
  const priorLabel = (activePreset && PRIOR_PERIOD_LABELS[activePreset]) || 'Prior Period';

  const axisTickStyle = { fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' };
  const yTickFormatter = (v) => isCurrency ? compactCurrencyAxis(v) : v.toLocaleString();
  const showLegend = hasMultiSeries ? activeSeriesNames.length > 0 : hasPrior;
  const chartChildren = [
    <CartesianGrid key="grid" strokeDasharray="3 3" vertical={false} stroke="var(--sl-border-secondary)" />,
    <XAxis key="x" dataKey="month" tick={axisTickStyle} axisLine={false} tickLine={false} minTickGap={16} />,
    <YAxis key="y" tick={axisTickStyle} axisLine={false} tickLine={false} tickFormatter={yTickFormatter} />,
    <Tooltip key="tip" content={<ChartTooltip isCurrency={isCurrency} countLabel={countLabel} />} cursor={resolvedChartType === 'bar' ? { fill: 'var(--sl-bg-secondary)' } : { stroke: 'var(--sl-border-primary)' }} />,
    showLegend && (
      <Legend key="legend" verticalAlign="top" align="right" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
    ),
  ];
  const chartMargin = { top: 5, right: 20, left: 10, bottom: 0 };

  let chart;
  if (displayData.length === 0) {
    chart = <EmptyState size="sm" title="No sales in this range" description="Try a wider date range or fewer filters." />;
  } else if (resolvedChartType === 'bar') {
    chart = (
      <BarChart data={displayData} margin={chartMargin}>
        {chartChildren}
        {hasMultiSeries
          ? activeSeriesNames.map((name, idx) => (
              <Bar key={name} dataKey={name} name={name} fill={SERIES_COLORS[idx % SERIES_COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={40} />
            ))
          : [
              hasPrior && <Bar key="prior" dataKey="prior_value" name={priorLabel} fill="#CBD5E1" radius={[4, 4, 0, 0]} maxBarSize={40} />,
              <Bar key="current" dataKey="value" name={currentLabel} fill={chartColor} radius={[4, 4, 0, 0]} maxBarSize={40} />,
            ]}
      </BarChart>
    );
  } else {
    chart = (
      <LineChart data={displayData} margin={chartMargin}>
        {chartChildren}
        {hasMultiSeries
          ? activeSeriesNames.map((name, idx) => (
              <Line key={name} type="monotone" dataKey={name} name={name} stroke={SERIES_COLORS[idx % SERIES_COLORS.length]} strokeWidth={2} dot={false} />
            ))
          : [
              hasPrior && <Line key="prior" type="monotone" dataKey="prior_value" name={priorLabel} stroke="#CBD5E1" strokeWidth={2} strokeDasharray="4 4" dot={false} />,
              <Line key="current" type="monotone" dataKey="value" name={currentLabel} stroke={chartColor} strokeWidth={2.5} dot={false} />,
            ]}
      </LineChart>
    );
  }

  return (
    <div className="mb-3">
      <Card
        header={(
          <CardHeader
            title={titleLink ? <a href={titleLink} style={{ color: 'inherit' }}>{title} →</a> : title}
            supportingText={description}
          />
        )}
      >
        <div className="d-flex align-items-baseline mb-3" style={{ gap: 'var(--setlist-spacing-md)' }}>
          <span style={{ fontSize: 'var(--setlist-display-xs-size)', fontWeight: 700, color: 'var(--sl-text-primary)' }}>{metricValue}</span>
          <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary)' }}>{metricLabel}</span>
        </div>
        {displayData.length === 0 ? chart : (
          <ResponsiveContainer width="100%" height={240}>
            {chart}
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
};

export default SalesCard;
